<script setup>
import { ref, computed, watch } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useTranslation } from 'i18next-vue';
import axios from '@/plugins/axios';
import { useSnackbar } from '@/composables/useSnackbar';
import { useLanguage } from '@/composables/useLanguage';
import { getDocumentLang, getDisplayedLang } from '@/utils/documentLang';
import LanguageSelector from '@/components/common/LanguageSelector.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import { DSButton } from '@/design-system/components';
import { apiErrorMessage } from '@/utils/apiErrorMessage';

const { t } = useTranslation();
const { currentLanguage } = useLanguage();

const props = defineProps({
	title: { type: String, required: true },
	// Initial markdown supplied by the parent (already fetched with the
	// current language). The dialog re-fetches on language change.
	content: { type: String, default: '' },
	showAcceptance: { type: Boolean, default: false },
	// Canonical (language-independent) version id sent on accept-terms.
	version: { type: String, default: '' },
	// Which legal document this dialog shows; used to refetch on lang change
	// and to read published_langs for the fallback notice.
	docType: { type: String, default: 'terms_of_use' },
	// When set, refetch from /versions/{versionId} instead of /active
	// (used by the "updated terms" review flow).
	versionId: { type: String, default: '' },
});

const emit = defineEmits(['close', 'accepted']);
const { showSnackbar } = useSnackbar();

const isAccepted = ref(false);
const isLoading = ref(false);
// Local copy of the markdown so the dialog can refetch on language change
// without mutating props.
const localContent = ref(props.content);
const publishedLangs = ref(['pt']);
// Gate the fallback notice until meta has loaded, so we don't flash
// "not available in <lang>" before we know what's actually published.
const metaLoaded = ref(false);

// True when the active locale has no published translation and the content
// shown is the PT canonical fallback.
const isFallback = computed(() => metaLoaded.value && !publishedLangs.value.includes(getDocumentLang()));

const renderedContent = computed(() => {
	if (!localContent.value) return `<p>${t('components.common.dialogs.legalDocuments.loading')}</p>`;
	try {
		const rawHtml = marked.parse(localContent.value);
		return DOMPurify.sanitize(rawHtml);
	} catch (e) {
		console.error("Error rendering markdown:", e);
		return `<p>${t('components.common.dialogs.legalDocuments.loadError')}</p>`;
	}
});

// Keep local content in sync if the parent swaps it (e.g. after its own fetch).
watch(() => props.content, (val) => { localContent.value = val; });

async function fetchPublishedLangs() {
	try {
		const meta = await axios.get(`/documents/${props.docType}/active-meta`);
		publishedLangs.value = meta.data?.published_langs || ['pt'];
	} catch (e) {
		// Best-effort: assume only PT is published so we still show the notice
		// for non-PT locales rather than silently implying a translation exists.
		console.warn(`Could not fetch active-meta for ${props.docType}:`, e);
		publishedLangs.value = ['pt'];
	}
}

async function fetchContent() {
	const lang = getDocumentLang();
	const url = props.versionId
		? `/documents/${props.docType}/versions/${props.versionId}`
		: `/documents/${props.docType}/active`;
	try {
		const response = await axios.get(url, { params: { lang } });
		localContent.value = response.data;
	} catch (e) {
		console.error(`Error fetching ${props.docType} (${lang}):`, e);
		// Keep whatever content we already had rather than blanking the dialog.
	}
}

// Refetch the document and metadata whenever the global language changes.
watch(currentLanguage, async () => {
	await Promise.all([fetchContent(), fetchPublishedLangs()]);
});

// Initial metadata load (content comes pre-fetched from the parent).
fetchPublishedLangs().finally(() => { metaLoaded.value = true; });

// SYN-2738 U6 — in acceptance mode, declining logs the user out (the parent
// handles `close` that way), so it must be explicit: the dialog is persistent
// (no click-outside / ESC) and "Recusar e sair" asks before emitting close.
const confirmationDialogRef = ref(null);
async function confirmDecline() {
	const result = await confirmationDialogRef.value?.show({
		dialogTitle: t('components.common.dialogs.legalDocuments.declineConfirm.title'),
		dialogMessage: t('components.common.dialogs.legalDocuments.declineConfirm.message'),
		confirmButtonText: t('components.common.dialogs.legalDocuments.declineConfirm.confirm'),
	});
	if (result?.confirmed) emit('close');
}

function onDialogModelUpdate(value) {
	if (!value && !props.showAcceptance) emit('close');
}

async function handleAccept() {
	if (!isAccepted.value) {
		showSnackbar(t('components.common.dialogs.legalDocuments.messages.mustAccept'), 'warning');
		return;
	}
	isLoading.value = true;
	try {
		// language = the language ACTUALLY displayed (requested lang if it has a
		// published translation, otherwise the PT fallback the backend served).
		const language = getDisplayedLang(publishedLangs.value);
		await axios.post('/users/me/accept-terms', { version: props.version, language });
		showSnackbar(t('components.common.dialogs.legalDocuments.messages.accepted'), 'success');
		emit('accepted');
	} catch (error) {
		console.error("Error accepting terms:", error);
		showSnackbar(apiErrorMessage(error, 'components.common.dialogs.legalDocuments.messages.acceptError', t), 'error');
		// Do not close the dialog on error, let the user retry or see the message
	} finally {
		isLoading.value = false;
	}
}
</script>

<template>
	<v-dialog :model-value="true" :persistent="showAcceptance" @update:model-value="onDialogModelUpdate" max-width="800px">
		<v-card :loading="isLoading">
			<div class="d-flex align-center pr-2">
				<v-card-title class="text-h5 flex-grow-1">{{ title }}</v-card-title>
				<LanguageSelector class="mr-2" />
			</div>
			<v-divider></v-divider>
			<v-alert
				v-if="isFallback"
				type="info"
				variant="tonal"
				density="compact"
				class="ma-4 mb-0"
			>
				{{ t('components.common.dialogs.legalDocuments.notPublishedNotice') }}
			</v-alert>
			<v-card-text class="doc-content" v-html="renderedContent"></v-card-text>
			<v-divider v-if="showAcceptance"></v-divider>
			<v-card-actions class="pa-4">
				<v-checkbox
					v-if="showAcceptance"
					v-model="isAccepted"
					:label="t('components.common.dialogs.legalDocuments.acceptCheckbox')"
					hide-details
					density="compact"
					:disabled="isLoading"
				></v-checkbox>
				<v-spacer></v-spacer>

				<template v-if="showAcceptance">
					<DSButton variant="ghost" data-testid="legal-decline" @click="confirmDecline" :disabled="isLoading">
						{{ t('components.common.dialogs.legalDocuments.buttons.declineAndLogout') }}
					</DSButton>
					<DSButton variant="primary" @click="handleAccept" :disabled="!isAccepted || isLoading" :loading="isLoading">
						{{ t('components.common.dialogs.legalDocuments.buttons.continue') }}
					</DSButton>
				</template>
				<template v-else>
					<DSButton variant="primary" @click="$emit('close')">
						{{ t('components.common.dialogs.legalDocuments.buttons.close') }}
					</DSButton>
				</template>
			</v-card-actions>
			<ConfirmationDialog v-if="showAcceptance" ref="confirmationDialogRef" />
		</v-card>
	</v-dialog>
</template>

<style scoped>
.doc-content {
	max-height: 70vh;
	overflow-y: auto;
	line-height: 1.6;
	color: var(--color-text-primary);
}
.doc-content :deep(h1),
.doc-content :deep(h2),
.doc-content :deep(h3) {
	margin-top: 1.2em;
	margin-bottom: 0.6em;
	font-weight: 600;
	line-height: 1.3;
	color: var(--color-text-primary);
}
.doc-content :deep(p) { margin-bottom: 1em; }
.doc-content :deep(ul), .doc-content :deep(ol) { margin-bottom: 1em; padding-left: 1.5em; }
.doc-content :deep(li) { margin-bottom: 0.4em; }
.doc-content :deep(strong) { font-weight: 600; }
</style>
